import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Flex } from "antd";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { toPrice } from "@utils/functions";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2.6rem",
  padding: "1.2rem 2rem",
});

const TitleWithContent = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1.6rem",
  borderBottom: "1px solid #f4f4f4",
  padding: "2.6rem 0",
  fontWeight: 400,
  fontSize: "1.4rem",

  ".title": {
    fontSize: "1.4rem",
    fontWeight: 600,
  },
});

const FeeBox = styled.div({
  display: "flex",
  justifyContent: "space-between",
  gap: "1.2rem",
  fontSize: "1.4rem",

  ".title": {
    color: "#757575",
    fontWeight: 500,
  },
});

const PolicyBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.8rem",
  fontWeight: theme.fontWeight.medium,
}));

type Props = {
  sellerInfo: any;
};
const SellerInfoPanel = ({ sellerInfo }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const lang = window.localStorage.getItem("language");

  return (
    <Container>
      <TitleWithContent>
        <span className="title">{t("소개")}</span>
        <span>{sellerInfo?.introduction}</span>
      </TitleWithContent>

      <TitleWithContent>
        <span className="title">{t("공지사항")}</span>
        <span>{sellerInfo?.notice}</span>
      </TitleWithContent>

      <TitleWithContent>
        <span className="title">{t("배송 정책")}</span>
        <FeeBox>
          <span className="title">{t("기본 배송비")}</span>
          <span>
            {sellerInfo?.shipping_fee
              ? `${toPrice(sellerInfo?.shipping_fee)}`
              : "-"}
          </span>
        </FeeBox>
        <FeeBox>
          <span className="title">{t("무료 배송")}</span>
          {!sellerInfo?.free_shipping_fee ? (
            <span>-</span>
          ) : (
            <Flex vertical gap="0.6rem">
              <Flex gap="0.4rem" justify="space-between">
                <Flex gap="0.4rem">
                  <span style={{ minWidth: "10rem", textAlign: "end" }}>
                    {t("구독 회원")}
                  </span>
                  <span> : </span>
                </Flex>
                {lang === "en" ? (
                  <Flex gap="0.4rem">
                    <span>{`${t("이상 구매 시")}`}</span>
                    <span>{toPrice(sellerInfo?.free_shipping_fee)}</span>
                  </Flex>
                ) : (
                  <Flex gap="0.4rem">
                    <span>{toPrice(sellerInfo?.free_shipping_fee)}</span>
                    <span>{`${t("이상 구매 시")}`}</span>
                  </Flex>
                )}
              </Flex>
              <Flex gap="0.4rem" justify="space-between">
                <Flex gap="0.4rem">
                  <span style={{ minWidth: "10rem", textAlign: "end" }}>
                    {t("비구독회원")}
                  </span>

                  <span> : </span>
                </Flex>

                {lang === "en" ? (
                  <Flex gap="0.4rem">
                    <span>{`${t("이상 구매 시")}`}</span>
                    <span>{toPrice(sellerInfo?.free_shipping_fee * 2)}</span>
                  </Flex>
                ) : (
                  <Flex gap="0.4rem">
                    <span>{toPrice(sellerInfo?.free_shipping_fee * 2)}</span>
                    <span>{`${t("이상 구매 시")}`}</span>
                  </Flex>
                )}
              </Flex>
            </Flex>
          )}
        </FeeBox>
      </TitleWithContent>

      <TitleWithContent>
        <span className="title">{t("교환 / 환불 정책")}</span>
        <Flex gap="2rem">
          <PolicyBox>
            <Image
              src={
                sellerInfo?.is_exchange_allowed
                  ? theme.image.success_box.default
                  : theme.image.fail_box.default
              }
              width="4.8rem"
              height="4.8rem"
            />

            <span>{t("교환")}</span>
          </PolicyBox>
          <PolicyBox>
            <Image
              src={
                sellerInfo?.is_return_allowed
                  ? theme.image.success_box.default
                  : theme.image.fail_box.default
              }
              width="4.8rem"
              height="4.8rem"
            />

            <span>{t("환불")}</span>
          </PolicyBox>
          <PolicyBox>
            <Image
              src={
                sellerInfo?.is_refund_allowed
                  ? theme.image.success_box.default
                  : theme.image.fail_box.default
              }
              width="4.8rem"
              height="4.8rem"
            />

            <span>{t("반품")}</span>
          </PolicyBox>
        </Flex>
      </TitleWithContent>
    </Container>
  );
};

export default SellerInfoPanel;
